import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, Eye, Loader2, Pencil, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { isWatched, toggleWatch, WATCH_MAX } from '../alerts';
import { WatchlistItem } from '../types';
import { sigLabel } from '../interpret';   // 📖 화면 이름 사전(TERMS.md) — 값은 그대로, 글자만 바꿈
import EarningsBadge from './EarningsBadge';

// 🎨 규격 V2(08-23) 부품 — 칩은 중립/링크/경고 세 가지. 색은 dc 토큰만(밝은 원색·보라 은퇴).
const CHIP = 'inline-flex items-center h-5 px-2 rounded-md border text-[11.5px] font-semibold whitespace-nowrap';
const MINICHIP = 'inline-flex items-center h-[18px] px-1.5 rounded-[5px] border text-[10.5px] font-medium whitespace-nowrap';
const TONE = {
  point:  'border-dc-point/35 bg-dc-point/[0.08] text-dc-point',
  up:     'border-dc-up/40 bg-dc-up/[0.08] text-dc-up',
  upSoft: 'border-dc-up/30 bg-dc-up/[0.06] text-dc-up',
  down:   'border-dc-down/35 bg-dc-down/[0.07] text-dc-down',
  warn:   'border-dc-warn/40 bg-dc-warn/[0.08] text-dc-warn',
  strong: 'border-dc-line2 bg-dc-bg2 text-dc-ink1',
  mute:   'border-dc-line1 bg-dc-bg1 text-dc-ink2',
  faint:  'border-dc-line1 bg-dc-base/70 text-dc-ink3',
} as const;

// 시그널 배지 — 강한 추세일수록 웜(상승색), 아직 모양만 잡힌 쪽은 중립, 힘 빠진 쪽은 흐림·주의색.
//   ⚠️ 초록(dc.ok)은 여기 안 쓴다 — 초록은 "확실히 좋음"(국면 양호·조건 통과)에만(규격 V2).
const SIGNAL_STYLE: Record<string, string> = {
  '신규진입':   TONE.mute,     // 화면 글자: 초기
  '완벽한추세': TONE.up,       // 매우 강함
  '강한추세':   TONE.upSoft,   // 강함
  '추세형성':   TONE.mute,     // 형성 중
  '단기추세':   TONE.mute,     // 짧음
  '단기관망':   TONE.faint,
  '관망':       TONE.faint,
  '위험':       TONE.warn,
  '데이터 부족': 'border-dc-line1 border-dashed bg-dc-base/70 text-dc-ink3',
  // 역추세
  '반등임박':   TONE.upSoft,
  '과매도':     TONE.down,
  '중립':       TONE.mute,
  '약세이탈':   TONE.warn,
};

// 시장 이름은 뜻이 없는 라벨 — 색깔 칩 남발 금지(규격 V2), 잉크 램프 하나로.
const MARKET_STYLE: Record<string, string> = {
  KOSPI: 'text-dc-ink2',
  KOSDAQ: 'text-dc-ink2',
};

function MarketCell({ market }: { market: string }) {
  const isStock = market === 'KOSPI' || market === 'KOSDAQ';
  const isUS = market === '미국';
  return (
    <div className="flex items-center gap-1 whitespace-nowrap">
      {!isStock && (
        <span className={`${MINICHIP} shrink-0 ${TONE.mute}`}>
          {isUS ? 'US' : 'ETF'}
        </span>
      )}
      <span className={`text-xs font-medium whitespace-nowrap ${MARKET_STYLE[market] ?? 'text-dc-ink2'}`}>
        {market}
      </span>
    </div>
  );
}

// 점수 — 등급별 색의 '칩'으로(막대 대신). 한눈에 강도 구분 + 깔끔.
function ScoreBar({ score, signal }: { score: number; signal?: string }) {
  if (signal === '데이터 부족') {
    return <span className="text-sm text-dc-ink3">─</span>;
  }
  const cls = score >= 70 ? TONE.strong : score >= 50 ? TONE.mute : TONE.faint;
  return (
    <span className={`inline-flex items-center justify-center min-w-[36px] px-2 py-1 rounded-md border text-sm font-bold tabular-nums ${cls}`}>
      {score}
    </span>
  );
}

function NoteCell({ ticker, note, onSave }: { ticker: string; note?: string; onSave: (t: string, n: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(note ?? '');
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  if (editing) {
    return (
      <div className="flex items-center gap-1 mt-0.5" onClick={stop}>
        <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="메모" autoFocus maxLength={200}
          className="w-32 bg-dc-bg1 border border-[#202A3C] rounded-md px-1.5 py-0.5 text-[11.5px] text-dc-ink2 outline-none focus:border-dc-point/50" />
        <button onClick={() => { onSave(ticker, val); setEditing(false); }} className={`${MINICHIP} ${TONE.point} font-semibold`}>저장</button>
        <button onClick={() => { setVal(note ?? ''); setEditing(false); }} className="text-dc-ink3 hover:text-dc-ink1"><X size={11} /></button>
      </div>
    );
  }
  return (
    <button onClick={(e) => { stop(e); setEditing(true); }} className="mt-0.5 text-left block" title="메모 편집">
      {note
        ? <span className="text-[10.5px] text-dc-point/80 italic">📝 {note}</span>
        : <span className="text-[10.5px] text-dc-ink3/60 hover:text-dc-ink2">📝 메모 추가</span>}
    </button>
  );
}

function HoldingCell({
  item,
  onEdit,
  onSell,
  onBuy,
}: {
  item: WatchlistItem;
  onEdit: (ticker: string, avg: number, qty: number) => void;
  onSell: (ticker: string, sellPrice: number, quantity: number) => void;
  onBuy: (ticker: string, buyPrice: number, quantity: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [selling, setSelling] = useState(false);
  const [buying, setBuying] = useState(false);
  const [avg, setAvg] = useState(String(item.avg_price ?? ''));
  const [qty, setQty] = useState(String(item.quantity ?? ''));
  const [sellPrice, setSellPrice] = useState(String(item.current_price ?? ''));
  const [sellQty, setSellQty] = useState(String(item.quantity ?? ''));
  const [buyPrice, setBuyPrice] = useState(String(item.current_price ?? ''));
  const [buyQty, setBuyQty] = useState('');

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // 입력 폼 공통 스타일 — 라벨(좌) + 넓은 입력(우)을 세로로 쌓아 큰 숫자도 여유롭게.
  const inputCls = 'flex-1 min-w-0 bg-dc-bg1 border border-[#202A3C] rounded-md px-2 py-1 text-xs text-dc-ink1 text-right tabular-nums focus:border-dc-point/50 focus:outline-none';
  const labelCls = 'text-[10.5px] text-dc-ink3 w-9 shrink-0';
  // 확정 버튼 — 매수=웜(up)·매도=쿨(down)·저장=앰버(규격 V2 색 관례). 파란 채움 버튼 은퇴.
  const actBtn = 'text-[11.5px] px-2.5 py-1 rounded-md border font-semibold transition-colors';

  if (buying) {
    const bp = Number(buyPrice) || 0;
    const bq = Number(buyQty) || 0;
    const cost = Math.round(bp * bq);
    return (
      <div className="flex flex-col gap-1.5 w-full max-w-[168px]" onClick={stop}>
        <div className="flex items-center gap-1.5">
          <span className={labelCls}>매수가</span>
          <input type="number" value={buyPrice} onChange={(e) => setBuyPrice(e.target.value)} placeholder="0" className={inputCls} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className={labelCls}>수량</span>
          <input type="number" value={buyQty} onChange={(e) => setBuyQty(e.target.value)} placeholder="0" className={inputCls} />
        </div>
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => { onBuy(item.ticker, bp, bq); setBuying(false); setBuyQty(''); }}
            className={`${actBtn} ${TONE.up} hover:border-dc-up/60`}
          >
            매수
          </button>
          <button onClick={() => setBuying(false)} className="text-dc-ink3 hover:text-dc-ink1 p-0.5">
            <X size={14} />
          </button>
        </div>
        {bp > 0 && bq > 0 && (
          <span className="text-[10.5px] tabular-nums text-dc-ink2 text-right">
            매입 {cost.toLocaleString('ko-KR')}원 · 현금 차감
          </span>
        )}
      </div>
    );
  }

  if (selling) {
    const sp = Number(sellPrice) || 0;
    const sq = Number(sellQty) || 0;
    const avgP = item.avg_price ?? 0;
    const realized = avgP > 0 ? Math.round((sp - avgP) * sq) : 0;
    const realizedUp = realized > 0;
    return (
      <div className="flex flex-col gap-1.5 w-full max-w-[168px]" onClick={stop}>
        <div className="flex items-center gap-1.5">
          <span className={labelCls}>매도가</span>
          <input type="number" value={sellPrice} onChange={(e) => setSellPrice(e.target.value)} placeholder="0" className={inputCls} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className={labelCls}>수량</span>
          <input type="number" value={sellQty} onChange={(e) => setSellQty(e.target.value)} placeholder="0" className={inputCls} />
        </div>
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => { onSell(item.ticker, sp, sq); setSelling(false); }}
            className={`${actBtn} ${TONE.down} hover:border-dc-down/60`}
          >
            매도
          </button>
          <button onClick={() => setSelling(false)} className="text-dc-ink3 hover:text-dc-ink1 p-0.5">
            <X size={14} />
          </button>
        </div>
        {sp > 0 && sq > 0 && (
          <span className={`text-[10.5px] tabular-nums text-right ${realizedUp ? 'text-dc-up' : 'text-dc-down'}`}>
            실현 {realizedUp ? '+' : ''}{realized.toLocaleString('ko-KR')} ({avgP > 0 ? ((sp / avgP - 1) * 100).toFixed(1) : 0}%)
          </span>
        )}
      </div>
    );
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1.5 w-full max-w-[168px]" onClick={stop}>
        <div className="flex items-center gap-1.5">
          <span className={labelCls}>평단가</span>
          <input type="number" value={avg} onChange={(e) => setAvg(e.target.value)} placeholder="0" className={inputCls} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className={labelCls}>수량</span>
          <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="0" className={inputCls} />
        </div>
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => { onEdit(item.ticker, Number(avg) || 0, Number(qty) || 0); setEditing(false); }}
            className={`${actBtn} border-dc-point/35 bg-dc-point/10 text-dc-point hover:border-dc-point/55`}
          >
            저장
          </button>
          <button onClick={() => setEditing(false)} className="text-dc-ink3 hover:text-dc-ink1 p-0.5">
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (item.pnl_pct == null) {
    return (
      <div className="flex items-center gap-2" onClick={stop}>
        <button
          onClick={(e) => { stop(e); setEditing(true); }}
          className="text-[11.5px] text-dc-ink3 hover:text-dc-point flex items-center gap-1 transition-colors"
          title="평단가·수량만 등록 (현금 안 건드림 — 과거 보유분용)"
        >
          <Pencil size={10} /> 보유 입력
        </button>
        <button
          onClick={() => { setBuyPrice(String(item.current_price ?? '')); setBuyQty(''); setBuying(true); }}
          className={`${MINICHIP} shrink-0 ${TONE.upSoft} hover:border-dc-up/55 transition-colors`}
          title="매수 기록 — 현금에서 차감"
        >
          매수
        </button>
      </div>
    );
  }

  const up = item.pnl_pct > 0;
  const color = up ? 'text-dc-up' : item.pnl_pct < 0 ? 'text-dc-down' : 'text-dc-ink2';
  return (
    <div className="flex items-center gap-1.5" onClick={stop}>
      <button
        onClick={() => { setEditing(true); }}
        className="flex flex-col items-start"
        title={`평단 ${item.avg_price?.toLocaleString('ko-KR')} × ${item.quantity}주 · 클릭하여 수정`}
      >
        <span className={`text-xs font-bold tabular-nums ${color}`}>
          {up ? '+' : ''}{item.pnl_pct.toFixed(2)}%
        </span>
        {item.pnl_amount != null && (
          <span className={`text-[10.5px] tabular-nums ${color}`}>
            {up ? '+' : ''}{item.pnl_amount.toLocaleString('ko-KR')}
          </span>
        )}
      </button>
      <button
        onClick={() => { setBuyPrice(String(item.current_price ?? '')); setBuyQty(''); setBuying(true); }}
        className={`${MINICHIP} shrink-0 ${TONE.upSoft} hover:border-dc-up/55 transition-colors`}
        title="추가 매수 기록 — 현금에서 차감 + 평단 재계산"
      >
        매수
      </button>
      <button
        onClick={() => { setSellPrice(String(item.current_price ?? '')); setSellQty(String(item.quantity ?? '')); setSelling(true); }}
        className={`${MINICHIP} shrink-0 ${TONE.down} hover:border-dc-down/55 transition-colors`}
        title="수익실현(매도) 기록"
      >
        매도
      </button>
    </div>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="border-b border-dc-hair/80">
          {Array.from({ length: cols }).map((_, j) => (
            <td key={j} className="px-3 py-2.5">
              <div className="skeleton h-3 rounded w-full" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

interface Props {
  data: WatchlistItem[];
  loading: boolean;
  onRowClick: (item: WatchlistItem) => void;
  onDelete: (ticker: string) => void;
  onEditHolding: (ticker: string, avgPrice: number, quantity: number) => void;
  onSellHolding: (ticker: string, sellPrice: number, quantity: number) => void;
  onBuyHolding: (ticker: string, buyPrice: number, quantity: number) => void;
  onSetNote: (ticker: string, note: string) => void;
  deletingTicker: string | null;
  changed?: Set<string>;   // ⑤ 지난 방문 이후 시그널 바뀐 종목(🔄 표시)
}

export default function WatchlistTable({ data, loading, onRowClick, onDelete, onEditHolding, onSellHolding, onBuyHolding, onSetNote, deletingTicker, changed }: Props) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'score', desc: true }]);
  const [watchVer, setWatchVer] = useState(0);   // ⑥ 👁 토글 즉시 반영용

  const columns = useMemo<ColumnDef<WatchlistItem>[]>(
    () => [
      {
        id: 'name',
        header: '종목명',
        accessorKey: 'name',
        cell: (info) => {
          const row = info.row.original;
          return (
            <div>
              <div className="text-dc-ink1 text-[13px] font-bold leading-tight">{row.name}</div>
              <div className="flex items-center gap-1">
                <span className="text-dc-ink3 text-[11.5px]">{row.ticker}</span>
                {row.earnings_dday != null && <EarningsBadge dday={row.earnings_dday} />}
                {/* ⑥ 관심 → 단타 장중 감시(👁) 원클릭 — 발굴→추적→장중 깔때기 연결 */}
                <button onClick={(e) => { e.stopPropagation(); const ok = toggleWatch(row.ticker, row.name); if (!ok) alert(`장중 감시는 최대 ${WATCH_MAX}개까지예요`); setWatchVer((v) => v + 1); }}
                  title={isWatched(row.ticker) ? '단타 장중 감시 중 (해제)' : '단타 탭에서 장중 감시(알림 받기)'}
                  className={`ml-0.5 ${isWatched(row.ticker) ? 'text-dc-point' : 'text-dc-ink3 hover:text-dc-ink2'}`}>
                  <Eye size={12} className={isWatched(row.ticker) ? 'fill-dc-point/20' : ''} />
                </button>
              </div>
              <NoteCell ticker={row.ticker} note={row.note} onSave={onSetNote} />
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'market',
        header: '시장',
        meta: { className: 'hidden lg:table-cell' },
        accessorKey: 'market',
        cell: (info) => <MarketCell market={info.getValue<string>()} />,
        size: 110,
      },
      {
        id: 'score',
        header: '점수',
        accessorKey: 'score',
        cell: (info) => <ScoreBar score={info.getValue<number>()} signal={info.row.original.signal} />,
        size: 110,
      },
      {
        id: 'signal',
        header: '시그널',
        accessorKey: 'signal',
        cell: (info) => {
          const sig = info.getValue<string>();
          const cls = SIGNAL_STYLE[sig] ?? TONE.mute;
          return (
            <span className="flex items-center gap-1 whitespace-nowrap">
              <span className={`${CHIP} ${cls}`}>{sigLabel(sig)}</span>
              {info.row.original.short_term && (
                <span className={`${MINICHIP} ${TONE.faint}`} title="상장 1년 미만 — 단기 추세 기준 점수">
                  단기
                </span>
              )}
              {/* ⑤ 지난 방문 이후 시그널 바뀐 종목 */}
              {changed?.has(info.row.original.ticker) && <span className="text-[10.5px] text-dc-point" title="지난 방문 이후 시그널이 바뀌었어요">🔄</span>}
            </span>
          );
        },
        size: 124,   // 고정 — 안 그러면 기본 150으로 잡혀 남는 폭을 흡수해 과하게 넓어짐
        enableSorting: false,
      },
      {
        id: 'reversion',
        header: '역추세',
        meta: { className: 'hidden lg:table-cell' },
        accessorKey: 'reversion_score',
        cell: (info) => {
          const v = info.getValue<number | null>();
          const sig = info.row.original.reversion_signal;
          if (v == null) return <span className="text-dc-ink3 text-xs">─</span>;
          const cls = sig ? (SIGNAL_STYLE[sig] ?? TONE.mute) : '';
          return (
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <span className="text-sm w-5 text-right tabular-nums text-dc-ink1">{v}</span>
              {sig && <span className={`${MINICHIP} ${cls}`}>{sigLabel(sig)}</span>}
            </span>
          );
        },
        size: 110,
      },
      {
        id: 'holding',
        header: '보유손익',
        accessorKey: 'pnl_pct',
        cell: (info) => <HoldingCell item={info.row.original} onEdit={onEditHolding} onSell={onSellHolding} onBuy={onBuyHolding} />,
        size: 195,   // 넓게 — 매수/매도 입력(가격·수량·버튼)이 한 줄에 안 끼게
        sortUndefined: 'last',
      },
      {
        id: 'current_price',
        header: '현재가',
        meta: { className: 'hidden sm:table-cell' },
        accessorKey: 'current_price',
        cell: (info) => (
          <span className="text-[13px] font-bold text-dc-ink1">
            {Number(info.getValue<number>()).toLocaleString('ko-KR')}
          </span>
        ),
      },
      {
        id: 'price_change_pct',
        header: '등락률',
        meta: { className: 'hidden md:table-cell' },
        accessorKey: 'price_change_pct',
        cell: (info) => {
          const v = info.getValue<number>();
          const color = v > 0 ? 'text-dc-up' : v < 0 ? 'text-dc-down' : 'text-dc-ink3';
          const arrow = v > 0 ? '▲' : v < 0 ? '▼' : '';
          return (
            <span className={`text-xs font-bold ${color}`}>
              {arrow}{Math.abs(v).toFixed(2)}%
            </span>
          );
        },
      },
      {
        id: 'rsi',
        header: 'RSI',
        meta: { className: 'hidden lg:table-cell' },
        accessorKey: 'rsi',
        cell: (info) => {
          const v = info.getValue<number>();
          const color = v >= 70 ? 'text-dc-up' : v <= 30 ? 'text-dc-down' : 'text-dc-ink2';
          return <span className={`text-xs ${color}`}>{v.toFixed(1)}</span>;
        },
      },
      {
        id: 'volume_ratio',
        header: '거래량비율',
        meta: { className: 'hidden lg:table-cell' },
        accessorKey: 'volume_ratio',
        cell: (info) => {
          const v = info.getValue<number>();
          // 평소의 2배 넘게 거래된 종목만 밝게 — 초록 강조는 은퇴(규격 V2)
          const cls = v >= 2.0 ? 'text-dc-ink1 font-bold' : 'text-dc-ink2';
          return <span className={`text-xs ${cls}`}>{v.toFixed(2)}x</span>;
        },
      },
      {
        id: 'ma_alignment',
        header: 'MA배열',
        meta: { className: 'hidden lg:table-cell' },
        accessorKey: 'ma_alignment',
        cell: (info) => {
          const v = info.getValue<string>();
          // 한국식 색 관례로 바로잡음 — 정배열(강세)=웜, 역배열(약세)=쿨. 옛 초록/빨강은 뜻이 뒤집혀 있었다.
          const color =
            v === '정배열' ? 'text-dc-up' : v === '역배열' ? 'text-dc-down' : 'text-dc-ink2';
          return <span className={`text-xs ${color}`}>{v}</span>;
        },
        enableSorting: false,
      },
      {
        id: 'delete',
        header: '',
        cell: (info) => {
          const ticker = info.row.original.ticker;
          return (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(ticker); }}
              disabled={deletingTicker === ticker}
              className="p-1 rounded-md text-dc-ink3 hover:text-dc-up hover:bg-dc-up/10 transition-colors disabled:opacity-50"
              title="관심종목 삭제"
            >
              {deletingTicker === ticker
                ? <Loader2 size={13} className="animate-spin" />
                : <X size={13} />
              }
            </button>
          );
        },
        size: 36,
        enableSorting: false,
      },
    ],
    // watchVer: 👁 토글 시 columns 재생성해 isWatched() 재평가(참조는 없지만 의도적 deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onDelete, onEditHolding, onSellHolding, onBuyHolding, onSetNote, deletingTicker, changed, watchVer]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (!loading && data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-2 text-dc-ink3">
        <p className="text-[13px]">관심종목을 추가해주세요</p>
        <p className="text-[11.5px]">위 검색창에서 종목명 또는 티커를 입력하세요</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-dc-hair">
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  className={`px-2 sm:px-3 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap ${(header.column.columnDef.meta as { className?: string } | undefined)?.className ?? ''}`}
                  style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                  onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                >
                  <span className={`flex items-center gap-1 ${header.column.getCanSort() ? 'cursor-pointer hover:text-dc-ink2' : ''}`}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() === 'asc' && <ChevronUp size={12} />}
                    {header.column.getIsSorted() === 'desc' && <ChevronDown size={12} />}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows cols={columns.length} />
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-dc-hair/80 hover:bg-white/[0.015] cursor-pointer transition-colors"
                onClick={() => onRowClick(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={`px-2 sm:px-3 py-2.5 ${(cell.column.columnDef.meta as { className?: string } | undefined)?.className ?? ''}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
